import { unsafeCSS, html } from 'lit';
import { state, property, queryAll } from 'lit/decorators.js';

import { PopBaseComponent } from '../../../base/pop-base-component.js';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants.js';
import { Constants } from './documents-table.constants.js';
import {
  Disclaimer,
  DisclaimerPositioning,
  Product,
  SortDirection,
  TableRecordVariant,
} from '../../product-center-table-wrapper/product-center-table-wrapper.model.js';
import { getCurrencySymbol } from '../../../common/utils/currency.js';
import { buildShareClassDescriptor } from '../../../common/utils/buildShareClass.js';
import { zustandStateToComponent } from '../../store/product-center.store';
import { TableTypeEnum } from '../../store/product-center.store.model';
import {
  checkProductCallback,
  handleCommonSortByDropdownChange,
  handleSortProductDataCells,
  handleUpdate,
  renderProductCellData,
  renderProductHeader,
  renderReportButton,
  renderTableCells,
  renderTableHeader,
  setAnalyticsAttributesForBeaconIconButton,
  getReportButtonAriaLabel,
} from '../../product-center-table-wrapper/product-center-table-wrapper.utils.js';
import Locale from '../../../common/utils/locale.js';

import style from '../common/css/table.scss?inline';

/**
 * @tag pop-pc-documents-table-component
 * @property {ProductCenterExhibit} [componentObject] Documents Table content
 * @attribute {ProductCenterExhibit} [data-component-object] Documents Table content
 */
export class PopDocumentsTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @property()
  private filteredProducts: Product[] = [];

  /**
   * @internal
   */
  @property()
  private sortByDropdownValue =
    ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value;

  /**
   * @internal
   */
  @property()
  selectedProducts: Product['productCode'][] = [];

  /**
   * @internal
   */
  private readonly tmdDisclaimers: Disclaimer[] = [];

  /**
   * @internal
   */
  private readonly productProfileDisclaimers: Disclaimer[] = [];

  @state()
  public sortDirectionState: SortDirection = SortDirection.ASCENDING;

  @property()
  tableHeaders: NodeListOf<Element> | undefined;

  @queryAll('beacon-icon-button.report-button')
  beaconIconButtons!: NodeListOf<HTMLElement>;

  constructor() {
    super();
    this.enableI18n = true;
  }

  /*
   * Component Methods
   */

  firstUpdated() {
    this.beaconIconButtons.forEach((button) => {
      setAnalyticsAttributesForBeaconIconButton(button);
    });
  }

  update(changedProperties: Map<string, unknown>) {
    handleUpdate(this, changedProperties);
    super.update(changedProperties);
  }

  private sortByDropdownChangeHandler(e: CustomEvent) {
    this.sortByDropdownValue = handleCommonSortByDropdownChange(
      e,
      ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value,
      this.tableHeaders,
      this.requestUpdate.bind(this),
    );
  }

  private sortProductDataCells(e: CustomEvent) {
    handleSortProductDataCells(this, e);
  }

  private renderTableHead() {
    return html`
      <tr>
        ${renderProductHeader(
          {
            rowspan: 1,
            sortByDropdownValue: this.sortByDropdownValue,
            sortByCallBack: this.sortByDropdownChangeHandler,
            onSort: this.sortProductDataCells,
            tableType: TableTypeEnum.DOCUMENTS,
          },
          new Locale(this.language, this.country),
        )}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.PRODUCT_DISCLOSURE_STATEMENT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.PRODUCT_DISCLOSURE_STATEMENT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.TARGET_MARKET_DETERMINATION,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.TARGET_MARKET_DETERMINATION)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
          infoText: `${this.tmdDisclaimers.map((disclaimer) => disclaimer.disclaimerText.html).join('')}`,
        })}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.PERFORMANCE_REPORT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.PERFORMANCE_REPORT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.PRODUCT_PROFILE,
          headerName: this.localize(Constants.TRANSLATION_KEYS.PRODUCT_PROFILE),
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
          infoText: `${this.productProfileDisclaimers.map((disclaimer) => disclaimer.disclaimerText.html).join('')}`,
        })}
        ${renderTableHeader({
          columnId: Constants.DOCUMENTS_COLUMN_IDS.QUARTERLY_REVIEW,
          headerName: this.localize(
            Constants.TRANSLATION_KEYS.QUARTERLY_REVIEW,
          ),
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
      </tr>
    `;
  }

  private renderTableBody() {
    return html`
      ${this.filteredProducts.map((product: Product) => {
        return html`
          <tr>
            ${renderProductCellData({
              index: JSON.stringify(product.productName),
              sortValue: product.productName[this.sortByDropdownValue] ?? '',
              checkboxLabel: product.productName.vehicleTypeName,
              fundName: product.productName.fundName,
              shareClass: product.productName.shareClass
                ? buildShareClassDescriptor(product.productName.shareClass)
                : '',
              currencySymbol: getCurrencySymbol(
                product.productName.baseCurrencyName,
              ),
              currencyName: product.productName.baseCurrencyName,
              apir: product.productName.apir ?? '',
              productCode: product.productCode,
              checkProductCallBack: (event: Event) =>
                checkProductCallback(event, TableTypeEnum.DOCUMENTS),
              isChecked: this.selectedProducts.includes(product.productCode),
              fundLink: product.productName.fundLink,
              showPopCheckbox: false,
              disclaimers: product.disclaimers ?? undefined,
              apirLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.APIR,
              ),
              noticeLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.NOTICE,
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.documents?.productDisclosureStatement,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.PRODUCT_DISCLOSURE_STATEMENT,
                  ),
                ),
                this.localize(
                  Constants.TRANSLATION_KEYS.PRODUCT_DISCLOSURE_STATEMENT,
                ),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.documents?.targetMarketDetermination,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.TARGET_MARKET_DETERMINATION,
                  ),
                ),
                this.localize(
                  Constants.TRANSLATION_KEYS.TARGET_MARKET_DETERMINATION,
                ),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.pricing?.performanceReportURL,
                getReportButtonAriaLabel(
                  product,
                  this.localize(Constants.TRANSLATION_KEYS.PERFORMANCE_REPORT),
                ),
                this.localize(Constants.TRANSLATION_KEYS.PERFORMANCE_REPORT),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.documents?.fundProfile,
                getReportButtonAriaLabel(
                  product,
                  this.localize(Constants.TRANSLATION_KEYS.PRODUCT_PROFILE),
                ),
                this.localize(Constants.TRANSLATION_KEYS.PRODUCT_PROFILE),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.pricing?.quarterlyReportURL,
                getReportButtonAriaLabel(
                  product,
                  this.localize(Constants.TRANSLATION_KEYS.QUARTERLY_REVIEW),
                ),
                this.localize(Constants.TRANSLATION_KEYS.QUARTERLY_REVIEW),
              ),
            })}
          </tr>
        `;
      })}
    `;
  }

  renderComponent() {
    return html` <beacon-table
      defer-until="visible"
      variant="fixed"
      density="standard"
    >
      <table>
        <thead>
          ${this.renderTableHead()}
        </thead>
        <tbody>
          ${this.renderTableBody()}
        </tbody>
      </table>
    </beacon-table>`;
  }

  render() {
    if (!this.filteredProducts && !this.componentObject) {
      return html``;
    }
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => {
        return {
          filteredProducts: productCenterState.getFilteredProducts(),
          selectedProducts:
            productCenterState.selectedProducts[TableTypeEnum.DOCUMENTS],
          tmdDisclaimers: productCenterState.getDisclaimersByPositioning(
            DisclaimerPositioning.TARGET_MARKET_DETERMINATION_TOOL_TIP,
          ),
          productProfileDisclaimers:
            productCenterState.getDisclaimersByPositioning(
              DisclaimerPositioning.PRODUCT_PROFILE_TOOL_TIP,
            ),
        };
      },
    },
    PopDocumentsTableComponent,
  ),
);
